import type { MenuItemEntity } from '@/types/entities';
import type { MenuAttributes } from '@/components/raven/global/SubNavigation';

const defaultSubMenu: MenuAttributes = {
	icon: undefined,
	menuItems: [],
	title: '',
	url: '',
};

export const isOrEndsWith = (maybeUrl: string, path: string) => {
	const normalizedPath = path.startsWith('/') ? path : `/${path}`;

	if (!maybeUrl) {
		return false;
	}

	if (
		maybeUrl.includes(`${normalizedPath}/`) ||
		maybeUrl.endsWith(normalizedPath)
	) {
		return true;
	}

	try {
		const url = new URL(maybeUrl);
		return (
			url.pathname === normalizedPath || url.pathname.endsWith(normalizedPath)
		);
	} catch {
		return maybeUrl.endsWith(normalizedPath);
	}
};

/**
 * Transforms a nested array of menu items into a flat list of structured submenu blocks.
 *
 * Each block represents a section of the submenu with its associated items. This function
 * recursively traverses the menu tree to gather valid children, ignoring items of type `postslist`,
 * and supports nested group structures.
 *
 * @param {MenuItemEntity[]} menuItems - The root-level array of menu items to process.
 * @returns {MenuAttributes[]} A flat array of structured submenu blocks.
 *
 */
export const generateSubmenu = (
	menuItems: MenuItemEntity[],
): MenuAttributes[] => {
	const result: MenuAttributes[] = [];

	if (!menuItems || menuItems.length === 0) {
		return [];
	}

	// Recursively process each menu item to build the submenu structure
	const processNode = (node: MenuItemEntity) => {
		const directChildren = Array.isArray(node?.children) ? node.children : [];
		// Only valid children that are not of type 'postslist'
		const allValidChildren: MenuItemEntity[] = [];

		// Process each direct child
		directChildren.forEach((child) => {
			const type = child.menu_item_type;

			// Skip children of type 'league' as they are not included in the submenu
			if (type === 'league') {
				return;
			}

			if (['group', 'horizontal-group'].includes(type || '')) {
				child.children?.forEach((grandchild) => {
					const grandchildType = grandchild.menu_item_type;
					if (grandchildType !== 'postslist' && grandchildType !== 'league') {
						allValidChildren.push(grandchild);
					}
				});
			} else if (type !== 'postslist') {
				allValidChildren.push(child);
			}
		});

		if (allValidChildren.length > 0) {
			result.push({
				icon: node.icon_display_secondary ? node.icon || undefined : undefined,
				menuItems: allValidChildren.map((child) => ({
					id: child.ID,
					text: child.title,
					url: child.url,
				})),
				title: node.title,
				url: node.url,
			});
		}

		// Recursively process children
		directChildren.forEach((child) => {
			const type = child.menu_item_type;

			// Skip children of type 'league' as they are not included in the submenu
			if (type === 'league') {
				return;
			}

			if (['group', 'horizontal-group'].includes(type || '')) {
				child.children?.forEach(processNode);
			} else {
				processNode(child);
			}
		});
	};

	// Start processing the top-level menu items
	menuItems.forEach(processNode);

	return result;
};

export const selectSubNavigation = (
	subNavigation: MenuAttributes[],
	pathname: string,
	lastVisitedPath: string,
): MenuAttributes => {
	// If the current path is the root path and the last visited path is also the root path,
	// return the default sub menu.
	if (pathname === '/' && (!lastVisitedPath || lastVisitedPath === '/')) {
		return defaultSubMenu;
	}

	// If the current path is the root path and the last visited path is not the root path,
	// return the sub menu that matches the last visited path.
	// This first find searches at parent level and if no matches are found, it searches at menuItems level.
	const selectedMenu =
		subNavigation.find((menu) => isOrEndsWith(menu.url, pathname)) ||
		subNavigation.find((menu) =>
			menu.menuItems.some((item) => isOrEndsWith(item.url, pathname)),
		);

	if (selectedMenu) {
		return selectedMenu;
	}

	// If the current path is not the root path, return the sub menu that matches the current path.
	if (lastVisitedPath && lastVisitedPath !== '/') {
		const previousMenu =
			subNavigation.find((menu) => isOrEndsWith(menu.url, lastVisitedPath)) ||
			subNavigation.find((menu) =>
				menu.menuItems.some((item) => isOrEndsWith(item.url, lastVisitedPath)),
			);

		if (previousMenu) {
			return previousMenu;
		}
	}

	return defaultSubMenu;
};
